import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import AbiShell from "@/pages/abi/AbiShell";
import { Button } from "@/components/ui/button";
import { Bell, Check, Loader2, ArrowRight, ShieldCheck, Sparkles } from "lucide-react";

const fmt = (iso) => (iso ? new Date(iso).toLocaleString("it-IT", { dateStyle: "short", timeStyle: "short" }) : "—");

export default function AbiCambiamenti() {
  const nav = useNavigate();
  const [rows, setRows] = useState([]);
  const [job, setJob] = useState({});
  const [filtro, setFiltro] = useState("nuovo");

  const load = useCallback(() => {
    api.get("/abi/cambiamenti", { params: filtro ? { stato: filtro } : {} }).then(({ data }) => setRows(data)).catch(() => {});
    api.get("/abi/monitoraggio/stato").then(({ data }) => setJob(data)).catch(() => {});
  }, [filtro]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (job.stato !== "in_corso") return undefined;
    const t = setInterval(load, 4000);
    return () => clearInterval(t);
  }, [job.stato, load]);

  const avvia = async (tipo) => {
    if (tipo === "completo" && !window.confirm(`Il ricontrollo completo rifà l'analisi con ricerca AI di ${job.clienti_totali || 0} clienti e consuma credito AI per ciascuno. Procedere?`)) return;
    try {
      const { data } = await api.post("/abi/monitoraggio/avvia", { tipo });
      toast.success(data.gia_in_corso ? "Un controllo è già in corso" : "Controllo avviato: i risultati compariranno qui");
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const gestito = async (r) => {
    try { await api.put(`/abi/cambiamenti/${r.id}`, { stato: "gestito" }); toast.success("Segnato come gestito"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const inCorso = job.stato === "in_corso";

  return (
    <AbiShell>
      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-10" data-testid="abi-cambiamenti-page">
        <div className="flex items-start justify-between gap-4 flex-wrap mb-6">
          <div>
            <h1 className="font-heading text-3xl font-semibold text-slate-900 flex items-center gap-2"><Bell className="h-6 w-6 text-violet-600" /> Cambiamenti rilevati</h1>
            <p className="text-sm text-slate-500 mt-1 max-w-2xl">Ogni mese controllo automaticamente le fonti ufficiali (ragione sociale, sede, forma giuridica, stato P.IVA) dei clienti analizzati. Il ricontrollo completo verifica anche dipendenti e cariche con ricerca AI. L'amministratore riceve un'email di riepilogo.</p>
          </div>
          <div className="flex gap-2 flex-wrap">
            <Button variant="outline" onClick={() => avvia("ufficiale")} disabled={inCorso} data-testid="abi-monitor-ufficiale"><ShieldCheck className="h-4 w-4 mr-1.5" /> Controlla fonti ufficiali ora</Button>
            <Button onClick={() => avvia("completo")} disabled={inCorso} className="bg-violet-600 hover:bg-violet-700 text-white" data-testid="abi-monitor-completo"><Sparkles className="h-4 w-4 mr-1.5" /> Ricontrolla tutto (con AI)</Button>
          </div>
        </div>

        <div className="rounded-xl border border-slate-200 bg-white px-5 py-3 mb-5 text-sm text-slate-600 flex flex-wrap gap-x-6 gap-y-1" data-testid="abi-monitor-stato">
          {inCorso ? (
            <span className="flex items-center gap-2 text-violet-700"><Loader2 className="h-4 w-4 animate-spin" /> Controllo {job.tipo === "completo" ? "completo" : "fonti ufficiali"} in corso: {job.fatti || 0}/{job.totale ?? "…"} clienti</span>
          ) : <span>Ultimo controllo: <b>{fmt(job.finished_at)}</b>{job.tipo ? ` (${job.tipo === "completo" ? "completo" : "fonti ufficiali"})` : ""}</span>}
          <span>Da gestire: <b data-testid="abi-monitor-da-gestire">{job.da_gestire ?? 0}</b></span>
          <span>Clienti monitorati: {job.clienti_ufficiali ?? 0} con P.IVA · {job.clienti_totali ?? 0} totali</span>
        </div>

        <div className="flex gap-1 mb-3">
          {[["nuovo", "Da gestire"], ["gestito", "Gestiti"], ["", "Tutti"]].map(([k, l]) => (
            <button key={k} onClick={() => setFiltro(k)} data-testid={`abi-cambi-filtro-${k || "tutti"}`} className={`px-3 py-1 rounded-full text-xs border transition-colors ${filtro === k ? "border-violet-500 bg-violet-100 text-violet-700 font-semibold" : "border-slate-200 bg-white text-slate-500 hover:bg-slate-50"}`}>{l}</button>
          ))}
        </div>

        <div className="rounded-xl border border-slate-200 bg-white overflow-x-auto">
          {rows.length === 0 ? <p className="text-center text-slate-400 py-12 text-sm" data-testid="abi-cambi-vuoto">Nessun cambiamento {filtro === "nuovo" ? "da gestire" : ""}.</p> : (
            <table className="w-full text-sm" data-testid="abi-cambi-tabella">
              <thead><tr className="text-left text-[11px] uppercase tracking-wide text-slate-400 border-b"><th className="px-4 py-2">Cliente</th><th className="px-4 py-2">Dato</th><th className="px-4 py-2">Prima</th><th className="px-4 py-2">Ora</th><th className="px-4 py-2">Rilevato</th><th className="px-4 py-2"></th></tr></thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.id} className="border-b border-slate-50 align-top" data-testid={`abi-cambio-${r.id}`}>
                    <td className="px-4 py-2.5 font-medium text-slate-800">{r.denominazione}<span className="block text-[11px] text-slate-400 font-normal">{r.fonte}</span></td>
                    <td className="px-4 py-2.5 text-slate-600">{r.campo}</td>
                    <td className="px-4 py-2.5 text-slate-400 max-w-[220px] break-words">{r.prima}</td>
                    <td className="px-4 py-2.5 text-slate-800 max-w-[220px] break-words">{r.dopo}</td>
                    <td className="px-4 py-2.5 text-xs text-slate-500 whitespace-nowrap">{fmt(r.rilevato_at)}</td>
                    <td className="px-4 py-2.5 whitespace-nowrap">
                      <Button size="sm" variant="outline" onClick={() => nav(`/business-intelligence/analisi/${r.analysis_id}`)} data-testid={`abi-cambio-apri-${r.id}`}>Proponi adeguamento <ArrowRight className="h-3.5 w-3.5 ml-1" /></Button>
                      {r.stato === "nuovo" && <Button size="sm" variant="ghost" className="ml-1 text-emerald-700" onClick={() => gestito(r)} data-testid={`abi-cambio-gestito-${r.id}`}><Check className="h-4 w-4" /></Button>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </main>
    </AbiShell>
  );
}
